import { lazy, Suspense, useEffect, type ReactElement } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { useAuthStore } from '@/stores/authStore';
import { Layout } from '@/components/layout/Layout';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { CookieNotice } from '@/components/common/CookieNotice';
import { PageLoader } from '@/components/common/LoadingSpinner';
import { SessionExpiredDialog } from '@/components/auth/SessionExpiredDialog';
import { discardStalePendingProfile, flushPendingProfile } from '@/lib/pendingProfile';
import { PUBLIC_SITE_ONLY } from '@/config/launch';

// Lazy-loaded pages (route-based code splitting)
const SignInPage = lazy(() => import('@/pages/SignInPage').then(m => ({ default: m.SignInPage })));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const QuestionnairePage = lazy(() => import('@/pages/QuestionnairePage').then(m => ({ default: m.QuestionnairePage })));
const UploadPage = lazy(() => import('@/pages/UploadPage').then(m => ({ default: m.UploadPage })));
const PricingPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.PricingPage })));
const ReUploadPage = lazy(() => import('@/pages/ReUploadPage').then(m => ({ default: m.ReUploadPage })));
const PublicPricingPage = lazy(() => import('@/pages/PublicPricingPage').then(m => ({ default: m.PublicPricingPage })));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import('@/pages/PaymentCancelPage').then(m => ({ default: m.PaymentCancelPage })));
const ProcessingPage = lazy(() => import('@/pages/ProcessingPage').then(m => ({ default: m.ProcessingPage })));
const OverviewPage = lazy(() => import('@/pages/OverviewPage').then(m => ({ default: m.OverviewPage })));
const HistoryPage = lazy(() => import('@/pages/HistoryPage').then(m => ({ default: m.HistoryPage })));
const OrderHistoryPage = lazy(() => import('@/pages/OrderHistoryPage').then(m => ({ default: m.OrderHistoryPage })));
const OrderDetailPage = lazy(() => import('@/pages/OrderDetailPage').then(m => ({ default: m.OrderDetailPage })));
const HistoryDemoPage = lazy(() => import('@/pages/HistoryDemoPage').then(m => ({ default: m.HistoryDemoPage })));
const DemoDashboardPage = lazy(() => import('@/pages/DemoDashboardPage').then(m => ({ default: m.DemoDashboardPage })));
const DashboardLayout = lazy(() => import('@/components/dashboard-v2/DashboardLayout').then(m => ({ default: m.DashboardLayout })));
const ContentReviewPage = lazy(() => import('@/pages/ContentReviewPage').then(m => ({ default: m.ContentReviewPage })));
const AffiliationsPage = lazy(() => import('@/pages/AffiliationsPage').then(m => ({ default: m.AffiliationsPage })));
const FootprintPage = lazy(() => import('@/pages/FootprintPage').then(m => ({ default: m.FootprintPage })));
const PrepareActPage = lazy(() => import('@/pages/PrepareActPage').then(m => ({ default: m.PrepareActPage })));
const IdentityPage = lazy(() => import('@/pages/IdentityPage').then(m => ({ default: m.IdentityPage })));
const IdentityLinksPage = lazy(() => import('@/pages/IdentityLinksPage').then(m => ({ default: m.IdentityLinksPage })));
const TravelPage = lazy(() => import('@/pages/TravelPage').then(m => ({ default: m.TravelPage })));
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfUsePage = lazy(() => import('@/pages/TermsOfUsePage').then(m => ({ default: m.TermsOfUsePage })));
// #162 §5.2/§6 row 20, #158 §6 item 4 — the statutory model cancellation
// form (reg 13(1)(b)). Public, unauthenticated: it must be available before
// the consumer is bound, not gated behind sign-in.
const CancellationFormPage = lazy(() => import('@/pages/CancellationFormPage').then(m => ({ default: m.CancellationFormPage })));
// #227 — every contact route and the company's details, public in every build.
const ContactPage = lazy(() => import('@/pages/ContactPage').then(m => ({ default: m.ContactPage })));
const HomePage = lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })));
const LaunchingSoonPage = lazy(() => import('@/pages/LaunchingSoonPage').then(m => ({ default: m.LaunchingSoonPage })));

/** An account page, or "opening soon" in a public-site-only build (#198). */
function accountPage(page: ReactElement): ReactElement {
  return PUBLIC_SITE_ONLY ? <LaunchingSoonPage /> : page;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Hash navigations are anchor jumps — leave them to the target page
    // (lazy-loaded pages scroll to the anchor once their content renders).
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  const { setUser, setLoading } = useAuthStore();

  // Questionnaire answers left on this device for more than 7 days are deleted
  // on every page load, whether or not anyone is signed in. Kept apart from the
  // session restore below so that nothing there can stop it running.
  useEffect(() => {
    discardStalePendingProfile();
  }, []);

  // Restore Cognito session on page load/refresh so ProtectedRoute
  // can make an accurate auth decision before rendering.
  useEffect(() => {
    async function restoreSession() {
      setLoading(true);
      try {
        const cognitoUser = await getCurrentUser();
        const attrs = await fetchUserAttributes();
        setUser({
          userId: cognitoUser.userId,
          email: attrs.email ?? '',
          name: attrs.name ?? '',
          emailVerified: attrs.email_verified === 'true',
        });
        // Questionnaire answers still waiting from an earlier failed save are
        // retried now. Not awaited, so the page is not held up by the request.
        void flushPendingProfile(attrs.email);
      } catch {
        // No active session — leave isAuthenticated as false.
      } finally {
        setLoading(false);
      }
    }
    restoreSession();
  }, [setLoading, setUser]);

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* The front page (Design A, 2026-09-27) — uses its own SiteHeader + SiteFooter */}
        <Route path="/" element={<HomePage />} />
        {/* Public, unauthenticated pricing page (dynamic cost-based pricing). */}
        <Route path="/pricing" element={<PublicPricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/legal/cancellation-form" element={<CancellationFormPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Auth flow + questionnaire (V3 styling — bring their own SiteHeader/Footer) */}
        <Route path="/sign-in" element={accountPage(<SignInPage />)} />
        {/* Accounts are created at the end of the questionnaire, so its
            answers are never skipped; the old sign-up address leads there. */}
        <Route path="/sign-up" element={<Navigate to="/get-started" replace />} />
        <Route path="/verify-email" element={accountPage(<VerifyEmailPage />)} />
        <Route path="/forgot-password" element={accountPage(<ForgotPasswordPage />)} />
        <Route path="/get-started" element={accountPage(<QuestionnairePage />)} />

        {/* Old front-page addresses (the V3 alias and the redesign previews,
            which were shared while choosing) now land on the front page. */}
        <Route path="/v3" element={<Navigate to="/" replace />} />
        <Route path="/redesign/a" element={<Navigate to="/" replace />} />
        <Route path="/redesign/b" element={<Navigate to="/" replace />} />

        {/* Public routes */}
        <Route element={<PublicLayout />}>
          {/* The dashboard-v2 experience on a hand-built post-Phase-A
              fixture. The :jobId segment must match data/demoReport.ts's
              DEMO_JOB_ID (hardcoded here so the fixture stays out of the
              main bundle). */}
          <Route path="/demo/dashboard/:jobId" element={<DemoDashboardPage />}>
            <Route index element={<OverviewPage />} />
            <Route path="content" element={<ContentReviewPage />} />
            <Route path="affiliations" element={<AffiliationsPage />} />
            <Route path="footprint" element={<FootprintPage />} />
            <Route path="travel" element={<TravelPage />} />
            <Route path="identity" element={<IdentityPage />} />
            <Route path="identity-links" element={<IdentityLinksPage />} />
            <Route path="prepare" element={<PrepareActPage />} />
          </Route>
          <Route path="/demo/dashboard" element={<Navigate to="/demo/dashboard/demo-visa-report" replace />} />
          <Route path="/demo/report" element={<Navigate to="/demo/dashboard/demo-visa-report" replace />} />
          <Route path="/demo/history" element={<HistoryDemoPage />} />
        </Route>

        {/* Protected authenticated routes */}
        <Route
          element={accountPage(
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          )}
        >
          <Route path="/upload" element={<UploadPage />} />
          {/* Post-upload, personalised price page (moved off /pricing, which is
              now the public marketing page). orderId rides in the URL path; the
              legacy router `state` is still read for back-compat. */}
          <Route path="/order/:orderId/price" element={<PricingPage />} />
          {/* #148 — free replacement upload after a rejected file. Uploads
              against the EXISTING order, so no second payment is taken. */}
          <Route path="/order/:orderId/reupload" element={<ReUploadPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/processing/:jobId" element={<ProcessingPage />} />
          <Route path="/processing" element={<ProcessingPage />} />
          {/* dashboard-v2 frame (report-quality Phase B): nested section
              routes share one DashboardLayout. */}
          <Route path="/dashboard/:jobId" element={<DashboardLayout />}>
            <Route index element={<OverviewPage />} />
            <Route path="content" element={<ContentReviewPage />} />
            <Route path="affiliations" element={<AffiliationsPage />} />
            <Route path="footprint" element={<FootprintPage />} />
            <Route path="travel" element={<TravelPage />} />
            <Route path="identity" element={<IdentityPage />} />
            <Route path="identity-links" element={<IdentityLinksPage />} />
            <Route path="prepare" element={<PrepareActPage />} />
          </Route>
          {/* #163 — two views of the same data. /history answers "where are my
              analyses?" (per platform, no price); /orders answers "what did I
              pay?" (per order, priced). Declared before /orders/:orderId so the
              literal path is not swallowed by the param route. */}
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/orders" element={<OrderHistoryPage />} />
          <Route path="/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>

      {/* Cookie notice */}
      <CookieNotice />

      {/* Session expired re-authentication dialog */}
      <SessionExpiredDialog />
    </ErrorBoundary>
  );
}

export default App;
